import { useEffect, useState, useCallback, type Dispatch, type SetStateAction } from 'react';
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { SendHorizontal, SquareCheckBig, RotateCcw, ChevronLeft, ChevronRight } from "lucide-react";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerTrigger,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer"
import { ReplyDrawerProps, replySchema, ReplySchemaType } from '.';
import { useForm, type UseFormReturn } from "react-hook-form";
import * as React from "react";
import { zodResolver } from '@hookform/resolvers/zod';
import { FieldRenderer } from '@/features/question/components/organisms/FieldRenderer';
import { useProjectStore, useUserStore } from '@/lib/zustand/useStore';
import { Form, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { getQuestionsInvalidatedByResponse } from '@/features/question/utils/findDependentQuestions';
import { groupInvalidatedQuestions } from '@/features/question/utils/groupInvalidatedQuestions';
import { QuestionWithDependents } from '@/features/question/hooks/useQuery/useGetQuestionsBySurveyId';
import { ReplyWarningDialog } from '@/features/question/components/organisms/ReplyWarningDialog';
import { useAddResponse } from '@/features/response/hooks/useMutation/useAddResponse';
import { useGetQuestionsBySurveyId } from '@/features/question/hooks/useQuery/useGetQuestionsBySurveyId';
import { Link, useParams } from 'react-router-dom';

type Props = Readonly<{
    questionData: ReplyDrawerProps["questionData"],
    questions: ReplyDrawerProps["questions"],
}>;

export const ResponseSection = ({
    questionData,
    questions,
}: Props) => {
    const { orgId, projectId, surveyId } = useParams();
    const userStore = useUserStore()
    const { project } = useProjectStore();

    const [submitDisabled, setSubmitDisabled] = useState(false);

    const form = useForm<ReplySchemaType>({
        resolver: zodResolver(replySchema),
        defaultValues: {
            response: '',
            additional: undefined,
        },
    });

    useEffect(() => {
        if(questionData?.id) {
            form.reset({
                response: '',
                additional: undefined,
            })
        }
    }, [questionData?.id]);

    // TODO: GET RID OF REFETCH. IT SHOULD BE IN useAddResponse
    const { refetch } = useGetQuestionsBySurveyId({
        surveyId: surveyId,
    });

    const { mutateAsync: addResponse, isPending: isPendingAddResponse } = useAddResponse();
    const replyHandler = (question_id: string) => async (formData: ReplySchemaType) => {

        await addResponse({ questions: questions, questionId: question_id, response: formData });

        form.reset({
            response: '',
            additional: undefined,
        });

        refetch();
    }

    const setAdditionalValue = useCallback((value: string | number | boolean | null | undefined | Array<string>) => {
        if (Array.isArray(value)) {
            value = value.join(',');
        }
        value = String(value).trim();
        form.setValue('additional', value);
    }, [form]);

    const [openWarningModal, setOpenWarningModal] = useState(false);
    const [questionsThatWillBeInvalidated, setQuestionsThatWillBeInvalidated] = useState<QuestionWithDependents[]>([]);

    const onSubmitHandler = async (values: ReplySchemaType) => {
        if (!questionData) return;

        if (values.additional) {
            const questionsThatWillBeInvalid = getQuestionsInvalidatedByResponse(
                questionData?.id,
                String(values.additional),
                questions ?? [],
                userStore?.id ?? 'mock-user-id',
            )
            const groupedQuestions = groupInvalidatedQuestions(questionsThatWillBeInvalid, questions ?? []);
            if (questionsThatWillBeInvalid.length > 0) {
                setQuestionsThatWillBeInvalidated(groupedQuestions);
                setOpenWarningModal(true);
                return;
            }
        }
        
        replyHandler(questionData.id)(values);
    }

    const onCloseWarningModal = () => {
        setOpenWarningModal(false);
        setQuestionsThatWillBeInvalidated([]);
    }

    const onSubmitHandlerWithWarning = (values: ReplySchemaType) => {
        if (!questionData) return;
        replyHandler(questionData.id)(values);
    }

    const disableButton = submitDisabled || isPendingAddResponse;

    // Previou/Next buttons
    const currentQuestionIndex = questions?.findIndex( question => question.id === questionData?.id);
    const isPreviousButtonDisabled = currentQuestionIndex === 0;
    const isNextButtonDisabled = ((questions?.length ?? 1) -  1) === currentQuestionIndex;

    const getQuestionID = ({ currentIndex, direction='prev' }: { currentIndex?: number, direction?: 'prev'|'next' }) => {
        if(!questions || currentIndex === undefined || currentIndex === null) return;

        let changeIndexBy = direction === 'prev' ? -1 : 1;

        let newIndex = currentIndex - changeIndexBy;
        if(newIndex < 0) newIndex = 0;

        return questions[newIndex]?.id;
    }

    if(!questionData || !questions) return;

    return (
        <>
            <ReplyWarningDialog
                open={openWarningModal}
                onOpenChange={onCloseWarningModal}
                onSubmit={() => onSubmitHandlerWithWarning(form.getValues())}
                questionsThatWillBeInvalidated={questionsThatWillBeInvalidated}
            />

            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmitHandler, err => console.log("[ResponseSection] errors: ",err))}>
                    <section className="flex gap-2 flex-wrap items-end">

                        <div className="border rounded-md py-2 border-transparent ">
                            <div className="h-[38px] flex items-center gap-1">
                                <Button 
                                    size="sm" 
                                    type="button"
                                    disabled={isPreviousButtonDisabled || disableButton || !getQuestionID({
                                        currentIndex: currentQuestionIndex, 
                                        direction: 'next'
                                    })}
                                    className="p-0"
                                >
                                    <Link to={`/organization/${orgId}/project/${projectId}/survey/${surveyId}/manage-survey/questions?questionId=${getQuestionID({
                                        currentIndex: currentQuestionIndex, 
                                        direction: 'next'
                                    })}`} className="px-1">
                                        <ChevronLeft size="18"/>
                                    </Link>
                                </Button>
                            
                                <Button 
                                    size="sm" 
                                    type="button"
                                    disabled={isNextButtonDisabled || disableButton || !getQuestionID({
                                        currentIndex: currentQuestionIndex, 
                                        direction: 'prev'
                                    })}
                                    className="p-0"
                                >
                                    <Link to={`/organization/${orgId}/project/${projectId}/survey/${surveyId}/manage-survey/questions?questionId=${getQuestionID({
                                        currentIndex: currentQuestionIndex, 
                                        direction: 'prev'
                                    })}`} className="px-1">
                                        <ChevronRight size="18"/>
                                    </Link>
                                </Button>
                           </div>
                        </div>
               
                        <ResponseComposer 
                            form={form}
                            questionData={questionData}
                            setAdditionalValue={setAdditionalValue}
                            setSubmitDisabled={setSubmitDisabled}
                            project={project}
                            disabled={disableButton}
                        />

                        <div className="border rounded-md py-2 border-transparent ">
                            <div className="h-[38px] flex items-center">
                                <Button 
                                    size="sm" 
                                    type="submit"
                                    disabled={disableButton}
                                >
                                    Send
                                    <SendHorizontal size="18"/>
                                </Button>
                            </div>
                        </div>
                  
                    </section>
                </form>
            </Form>
        </>
    )
}

// TODO: Adjust types
type ResponseComposerProps = {
    form: UseFormReturn<ReplySchemaType>;
    questionData: Readonly<ReplyDrawerProps["questionData"]>;
    setAdditionalValue: (value: string | number | boolean | string[] | null | undefined) => void;
    setSubmitDisabled: Dispatch<SetStateAction<boolean>>
    project: any; 
    disabled: boolean;
}

export default function ResponseComposer({
    form,
    questionData,
    setAdditionalValue,
    setSubmitDisabled,
    project,
    disabled,
}: ResponseComposerProps) {
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
//   const [value, setValue] = React.useState("");

  const resizeTextarea = React.useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = "38px";
    textarea.style.height = `${Math.max(textarea.scrollHeight, 38)}px`;
  }, []);

  const watchResponse = form.watch('response', '');
  const watchValue = form.watch('additional', undefined)

//   React.useLayoutEffect(() => {
//     resizeTextarea();
//   }, [value, resizeTextarea]);


    React.useLayoutEffect(() => {
        resizeTextarea();
    }, [watchResponse, resizeTextarea]);

  return (
    <div className="grow">
        <div className="flex items-end gap-2 rounded-md border bg-background p-2 shadow-sm">

            {/* <Textarea
                ref={textareaRef}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Write a comment..."
                rows={1}
                className="min-h-[38px] resize-none overflow-hidden border-0 bg-transparent px-3 py-2 text-sm leading-5 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
            /> */}

            <FormField
                name='response'
                control={form.control}
                render={({ field }) => (
                    <FormItem className="space-y-0 w-full">
                        <Textarea
                            {...field}
                            ref={textareaRef}
                            placeholder="Write a comment..."
                            rows={1}
                            className="min-h-[38px] resize-none overflow-hidden border-0 bg-transparent px-3 py-2 text-sm leading-5 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                        />
                        <FormMessage className="ms-3"/>
                    </FormItem>
                )}
            />

            <div className="flex items-center gap-1">
                <Drawer>
                    <DrawerTrigger>
                        <div className="h-[38px] flex items-center">

                            <Button 
                                size="sm" 
                                type="button"
                                aria-label="Select value"
                                disabled={disabled}
                                className="relative"
                            >
                                Select value
                                <SquareCheckBig size="18"/>

                                {watchValue && (<div className="h-2 w-2 rounded-full bg-red-500 absolute -end-0.5 -top-0.5"></div>)}
                            </Button>
                        </div>
                    </DrawerTrigger>

                    <DrawerContent className="flex justify-center">
                        <DrawerHeader className="border-b pb-2">
                            {/* <div className="flex w-full justify-center"> */}
                                {/* <div className="max-w-[600px] w-full flex justify-between gap-2 items-center"> */}
                                    <DrawerTitle className="text-center">Provide value</DrawerTitle>  
                                {/* </div> */}
                            {/* </div> */}

                            <DrawerDescription></DrawerDescription>
                        </DrawerHeader>
                        
                        <div className="no-scrollbar overflow-y-auto px-4 flex justify-center mt-3">
                            <div className="max-w-[600px] w-full p-1">
                                <FieldRenderer
                                    // form={form}

                                    currentValue={watchValue}

                                    question={questionData}
                                    setFormValue={setAdditionalValue}
                                    setSubmitDisabled={setSubmitDisabled}
                                    crmSystem={project?.crm_system}
                                />
                            </div>
                        </div>
                
                        <DrawerFooter>
                            <DrawerClose>
                                <div className="w-full flex justify-center">
                                    <Button type="button" size="sm" className="max-w-[600px] grow" >Close</Button>
                                </div>
                            </DrawerClose>
                        </DrawerFooter>

                    </DrawerContent>
                </Drawer>
                <Button
                    variant="outline"
                    size="sm"
                    type="button"
                    className="gap-1"
                    onClick={() => form.setValue("additional", undefined)}
                >
                    <RotateCcw size="14"/>
                </Button>
            </div>

        </div>
    </div>
  );
}
